/**
 * API client for the ACADS section (Kolkata tuition business).
 *
 * Backend: oh_acads Django app, alfred-suffixed endpoints under
 * /api/v1/oh_acads/. The data is a read-only mirror of the finance team's
 * Google Sheets (synced hourly + on demand); every view is computed from it.
 */
import { sentryLogger } from 'sentry'

import { apiEndpoints } from './common'

import { makeRequestUrl } from '../app/helpers'
import { get, patch, post, remove } from '../request'

// Re-pull all four source sheets into the mirror. Returns the run summary.
const runAcadsSync = async () => {
  try {
    const response = await post(`${apiEndpoints.acads}/sync/alfred`, {})
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in running acads sync')
    throw error
  }
}

// Monthly reconciliation — masterdata vs daily receipts, with drill-downs.
const fetchAcadsReco = async (month) => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.acads}/reco/alfred`, { month })
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching acads reco')
    throw error
  }
}

// Daily receipts for a month (optionally one centre / free-text search).
const fetchAcadsReceipts = async (month, centre, q) => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.acads}/receipts/alfred`, {
        month,
        centre,
        q
      })
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching acads receipts')
    throw error
  }
}

// Enrolment roster (teacher / centre / search filters, all optional).
const fetchAcadsStudents = async (teacher, centre, q) => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.acads}/students/alfred`, {
        teacher,
        centre,
        q
      })
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching acads students')
    throw error
  }
}

// Per-teacher payout view for a month, or April up to it when cumulative
// (split factors applied server-side).
const fetchAcadsDispatch = async (month, cumulative = false) => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.acads}/dispatch/alfred`, {
        month,
        ...(cumulative ? { cumulative: 1 } : {})
      })
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching acads dispatch')
    throw error
  }
}

// Save how a teacher's payout for a month went: bank and cash.
const saveAcadsDispatchSplit = async (payload) => {
  try {
    const response = await post(
      `${apiEndpoints.acads}/dispatch/alfred`,
      payload
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in saving acads dispatch split')
    throw error
  }
}

// ---------------------------------------------------------------- entry side
// Alfred's own record from September 2026 on: the catalogue, the monthly grid
// and the dashboard over it. Everything above this line reads the parked sheet
// mirror instead.

// Month at a glance — totals, splits, and a row per teacher.
// basis: 'revenue' (fees for the period) or 'receipt' (money in during it);
// ytd runs the period from April up to the month.
const fetchAcadsSummary = async (month, basis = 'revenue', ytd = false) => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.acads}/summary/alfred`, {
        month,
        basis,
        ...(ytd ? { ytd: 1 } : {})
      })
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching acads summary')
    throw error
  }
}

// Close out the ordinary remainder of a month. With preview it only counts.
const bulkMarkAcadsPaid = async (payload) => {
  try {
    const response = await post(
      `${apiEndpoints.acads}/entry/bulk_paid/alfred`,
      payload
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in bulk marking acads fees paid')
    throw error
  }
}

// HQ Finance: the month's exceptions and its sign-off state.
const fetchAcadsReview = async (month) => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.acads}/review/alfred`, { month })
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching acads review')
    throw error
  }
}

// HQ Finance closes a month, or reopens one with a reason.
const setAcadsSignoff = async (payload) => {
  try {
    const response = await post(`${apiEndpoints.acads}/signoff/alfred`, payload)
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in updating acads sign-off')
    throw error
  }
}

// Teachers entry can be filed against.
const fetchAcadsTeachers = async () => {
  try {
    const response = await get(`${apiEndpoints.acads}/teachers/alfred`)
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching acads teachers')
    throw error
  }
}

// Load the roster from the annual student form. Safe to repeat.
const seedAcadsCatalogue = async () => {
  try {
    const response = await post(
      `${apiEndpoints.acads}/catalogue/seed/alfred`,
      {}
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in loading acads students from the form')
    throw error
  }
}

const createAcadsTeacher = async (name) => {
  try {
    const response = await post(`${apiEndpoints.acads}/teachers/alfred`, {
      name
    })
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in creating acads teacher')
    throw error
  }
}

// The monthly fee grid: a row per rostered student, pre-filled with whatever
// has been entered so far.
const fetchAcadsEntry = async (month, teacher) => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.acads}/entry/alfred`, { month, teacher })
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching acads entry grid')
    throw error
  }
}

// Save edited rows; each row carries its own enrolment and month.
const saveAcadsEntry = async (month, rows) => {
  try {
    const response = await post(`${apiEndpoints.acads}/entry/alfred`, {
      month,
      rows
    })
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in saving acads entry')
    throw error
  }
}

// The teacher-wise, student-wise catalogue.
const fetchAcadsCatalogue = async (teacher, includeDropped) => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.acads}/catalogue/alfred`, {
        teacher,
        include_dropped: includeDropped ? 1 : undefined
      })
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching acads catalogue')
    throw error
  }
}

const createAcadsStudent = async (payload) => {
  try {
    const response = await post(
      `${apiEndpoints.acads}/catalogue/alfred`,
      payload
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in creating acads student')
    throw error
  }
}

const updateAcadsStudent = async (payload) => {
  try {
    const response = await patch(
      `${apiEndpoints.acads}/catalogue/alfred`,
      payload
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in updating acads student')
    throw error
  }
}

// Drops from future months; earlier entries are kept, so this is never a
// delete. Pass { restore: true } to put the student back on the roster.
const dropAcadsStudent = async (payload) => {
  try {
    const response = await remove(
      `${apiEndpoints.acads}/catalogue/alfred`,
      {},
      payload
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in dropping acads student')
    throw error
  }
}

// Receipt photo / UPI screenshot against a saved fee row.
// Every active student with one cell per fee month — feeds the collect card
// and the teacher sheet.
const fetchAcadsLedger = async (year, withLeft) => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.acads}/ledger/alfred`, {
        ...(year ? { year } : {}),
        ...(withLeft ? { include_left: 1 } : {})
      })
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching acads ledger')
    throw error
  }
}

// Record one payment; the backend spreads it over the oldest unpaid months.
// `file` is the payment screenshot or receipt photo — the backend refuses a
// non-cash payment without one.
const collectAcadsFee = async (payload, file) => {
  try {
    const body = new FormData()
    Object.keys(payload).forEach((key) => body.append(key, payload[key]))
    if (file) body.append('file', file)
    const response = await post(`${apiEndpoints.acads}/collect/alfred`, body, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in collecting acads fee')
    throw error
  }
}

// The month's reconciliation for a centre (blank = both): cash book, bank
// lines to check, OH share from teachers.
const fetchAcadsCashbook = async (month, centre) => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.acads}/cashbook/alfred`, { month, centre })
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching acads cashbook')
    throw error
  }
}

// A cash-book line, or { kind: 'opening', centre, month, amount }.
const addAcadsCashEntry = async (payload, file) => {
  try {
    const body = new FormData()
    Object.keys(payload).forEach((key) => body.append(key, payload[key]))
    if (file) body.append('file', file)
    const response = await post(`${apiEndpoints.acads}/cashbook/alfred`, body, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in adding acads cash entry')
    throw error
  }
}

const removeAcadsCashEntry = async (uuid) => {
  try {
    const response = await remove(
      `${apiEndpoints.acads}/cashbook/alfred`,
      {},
      { uuid }
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in removing acads cash entry')
    throw error
  }
}

// HQ: { source: 'fee' | 'entry', uuid, status: 'matched' | 'doubtful' | '', note }
const setAcadsBankCheck = async (payload) => {
  try {
    const response = await post(
      `${apiEndpoints.acads}/cashbook/check/alfred`,
      payload
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in saving acads bank check')
    throw error
  }
}

// Every month since Alfred took over, with its freeze state.
const fetchAcadsFreeze = async (year) => {
  try {
    const response = await get(
      makeRequestUrl(
        `${apiEndpoints.acads}/freeze/alfred`,
        year ? { year } : {}
      )
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching acads freeze')
    throw error
  }
}

// HQ: { month, action: 'freeze' | 'unfreeze' | 'decline' }
const setAcadsFreeze = async (payload) => {
  try {
    const response = await post(`${apiEndpoints.acads}/freeze/alfred`, payload)
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in changing acads freeze')
    throw error
  }
}

// Centre: ask HQ to reopen a frozen month. { month, reason }
const requestAcadsReopen = async (payload) => {
  try {
    const response = await post(
      `${apiEndpoints.acads}/freeze/request/alfred`,
      payload
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in requesting acads reopen')
    throw error
  }
}

// Correct a saved payment: its amount, mode or date.
const amendAcadsPayment = async (payload, file) => {
  try {
    const body = new FormData()
    Object.keys(payload).forEach((key) => body.append(key, payload[key]))
    if (file) body.append('file', file)
    const response = await patch(`${apiEndpoints.acads}/payment/alfred`, body, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in changing acads payment')
    throw error
  }
}

// Take a wrongly saved payment off its line; the line goes back to unpaid.
const cancelAcadsPayment = async (uuid) => {
  try {
    const response = await remove(
      `${apiEndpoints.acads}/payment/alfred`,
      {},
      { uuid }
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in cancelling acads payment')
    throw error
  }
}

// Fees received since Alfred took over, one line per fee row.
const fetchAcadsRegister = async () => {
  try {
    const response = await get(`${apiEndpoints.acads}/register/alfred`)
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching acads register')
    throw error
  }
}

const attachAcadsReceipt = async (feeRowUuid, file) => {
  try {
    const body = new FormData()
    body.append('fee_row', feeRowUuid)
    body.append('file', file)
    const response = await post(`${apiEndpoints.acads}/receipt/alfred`, body, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in attaching acads receipt')
    throw error
  }
}

export {
  addAcadsCashEntry,
  fetchAcadsCashbook,
  removeAcadsCashEntry,
  setAcadsBankCheck,
  amendAcadsPayment,
  attachAcadsReceipt,
  bulkMarkAcadsPaid,
  cancelAcadsPayment,
  collectAcadsFee,
  createAcadsStudent,
  createAcadsTeacher,
  dropAcadsStudent,
  fetchAcadsCatalogue,
  fetchAcadsDispatch,
  saveAcadsDispatchSplit,
  fetchAcadsFreeze,
  fetchAcadsEntry,
  fetchAcadsLedger,
  fetchAcadsRegister,
  fetchAcadsReview,
  fetchAcadsSummary,
  fetchAcadsTeachers,
  requestAcadsReopen,
  saveAcadsEntry,
  seedAcadsCatalogue,
  setAcadsFreeze,
  setAcadsSignoff,
  updateAcadsStudent,
  fetchAcadsReceipts,
  fetchAcadsReco,
  fetchAcadsStudents,
  runAcadsSync
}
